import { SolidLeaf, SolidContainer } from "@ldo/connected-solid";
import { useLdo, useResource } from "@ldo/solid-react";
import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useParams } from "react-router-dom";
import { useWsContext, WsContext, WsContextSingleton } from "../components/WsContext";
import { Header } from "../layout/Header";
import { cap } from "../lib/cap";
import { rdf, spoty } from "../lib/ns";
import { makeNamedNode } from "../lib/nodes";
import { WorkspaceUri } from "../App";
import { lastPart } from "../lib/lastPart";
// CSS
import "./WorkspaceLayout.css";
import { useAppContext } from "../components/AppContext";

export const WorkspaceLayout: FunctionComponent= () => {
  const params = useParams();
  const workspaceUri = params.wid as WorkspaceUri;
  console.debug("WorkspaceLayout rendering", workspaceUri);
  const workspaceRes = useResource(workspaceUri);
  const [wcs, setWcs] = useState(new WsContextSingleton(workspaceRes));
  const { dataset, getResource } = useLdo();
  const { t } = useTranslation("spoty");
  const [ appCtx ] = useAppContext();

  useEffect(() => {
    console.debug("WorkspaceLayout's effect: loading ", workspaceRes.uri);

    // To avoid strange behaviours, we maintain a local version of wcs during Loading.
    let myWcs = new WsContextSingleton(workspaceRes);
    setWcs({ ...myWcs });

    const incDiscovered = () => {
      // console.debug("=== WorkspaceLayout effect changing 1 wcs", workspaceRes.uri);
      myWcs.loadIndicator[1] += 1;
      setWcs({ ...myWcs });
    };
      
    const incLoaded = () => {
      // console.debug("=== WorkspaceLayout effect changing 2 wcs", workspaceRes.uri);
      myWcs.loadIndicator[0] += 1;
      setWcs({ ...myWcs });
    };

    const pushSentenceUris = (uris: string[]) => {
      // console.debug("=== WorkspaceLayout effect changing 3 wcs", workspaceRes.uri);
      myWcs.sentenceUris.push(...uris);
      setWcs({ ...myWcs });
    };

    // load asynchronously all sub-containers and resources of the workspace
    // IMPORTANT: after each await and in each then(), `abort` must be tested

    let abort = false;
    let finished = false;

    async function loadRecursively(resource: SolidLeaf | SolidContainer) {
      const result = await resource.readIfUnfetched();
      if (abort || result.isError) { return; }

      if (resource.type === "SolidContainer") {
        for (let child of resource.children()) {
          incDiscovered();
          loadRecursively(child);
        }

      } else {
          const resNode = makeNamedNode(resource.uri);
          // index sentences from this collection (if any)
          const uris = dataset.match(null, RDF_TYPE, SPOTY_SENTENCE, resNode).toArray().map(q => q.subject.value);
          pushSentenceUris(uris);
          // fetch remote sentences (if any)
          const remotes = dataset.match(resNode, RDFS_SEE_ALSO, null, resNode).toArray().map(q => q.object.value);
          for (const remoteUri of remotes) {
              const remoteResource = getResource(remoteUri);
              if (remoteResource.type !== 'InvalidIdentifierResource') {
                incDiscovered();
                loadRecursively(remoteResource);
              }
          }
      }
      incLoaded();
    }
    loadRecursively(workspaceRes);

    if (abort) return;
    console.debug("WorkspaceLayout effect finishing", workspaceRes.uri);
    finished = true;

    return () => { 
      if (!finished) {
        console.debug("WorkspaceLayout effect aborting");
        abort = true;
      }
    };
  }, [dataset, getResource, setWcs, workspaceRes]);

  useEffect(() => {
    if (!wcs.isLoading() && appCtx.sparqlEngine && !wcs.sparqlEnginePopulated) {
        console.debug("WorkspaceLayout's effect: populating sparqlEngine");
        const quads = Array.from(dataset).map(q => ({
            subject: { termType: q.subject.termType, value: q.subject.value },
            predicate: { termType: q.predicate.termType, value: q.predicate.value },
            object: {
                termType: q.object.termType,
                value: q.object.value,
                language: 'language' in q.object ? (q.object as any).language : undefined,
                datatype: 'datatype' in q.object ? (q.object as any).datatype?.value : undefined,
            },
        }));

        appCtx.sparqlEngine.populate(
          quads,
          (size) => {
            console.log(`WorksaceLayout's effect: populating sparqlEngine → done (${size} quads)`);
            setWcs(old => { return { ...old, sparqlEnginePopulated: true } });
          },
          console.error,
        );
    }
  }, [dataset, appCtx.sparqlEngine, wcs]);

  const label = lastPart(workspaceUri);

  return <>
    <Header>{cap(t('workspace'))} {label}</Header>
    <WsContext value={wcs}>
      <WorkspaceToolbar uri={workspaceUri} />
      <main>
        <Outlet />
      </main>
    </WsContext>
  </>;
}


const WorkspaceToolbar: FunctionComponent<{
  uri: WorkspaceUri,
}> = ({
  uri,
}) => {
  const { t } = useTranslation('spoty');
  const wcs = useWsContext();
  const loadIndicator = (
    wcs.isLoading()
    ? <abbr title={`${t("loading sentences")} ${wcs.loadIndicator.join('/')}`} style={{ display: 'inline-block' }}> ⏳</abbr>
    // ? ` ${wcs.loadIndicator.join('/')} ⏳` // FOR DEBUGGGING
    : null
  );
  const prefix = `/w/${encodeURIComponent(uri)}/`;
  return <nav className='WorkspaceToolbar'>
    <ul>
      <ToolbarButton to={`${prefix}`} exact>{cap(t("all sentences"))}
        <span className="loadIndicator">{loadIndicator}</span></ToolbarButton>
      <ToolbarButton to={`${prefix}lang/`}>{cap(t('language', { count: 99 }))}</ToolbarButton>
      <ToolbarButton to={`${prefix}stim/`}>{cap(t('stimulus', { count: 99}))}</ToolbarButton>
      <ToolbarButton to={`${prefix}map`}>{cap(t('map'))}</ToolbarButton>    
      <ToolbarButton to={`${prefix}sparnatural`}>{cap(t('sparnatural')) + " (experimental)"}</ToolbarButton>    
    </ul>
  </nav>;
}

const ToolbarButton: FunctionComponent<{
  to: string,
  exact?: boolean,
  children: ReactNode,
}> = ({
  to,
  exact,
  children,
}) => {
  const isCurrent = exact
    ? window.location.pathname.endsWith(to)
    : window.location.pathname.includes(to);
  return <li className={isCurrent ? "current" : ""}>
    <Link to={to}>{children}</Link>
  </li>;
}

const RDF_TYPE = makeNamedNode(rdf.type);
const SPOTY_SENTENCE = makeNamedNode(spoty.Sentence);
const RDFS_SEE_ALSO = makeNamedNode("http://www.w3.org/2000/01/rdf-schema#seeAlso");
